import { useQuery } from "@tanstack/react-query";

import { DecisionStrip } from "@/components/underwriting/DecisionStrip";
import { fmtMoneyShort } from "@/utils/optimisticAnalysisRecompute";
import type { RevenueLine } from "@/types/underwriting";

import { quickLookQueryOptions } from "../../assistant/api";

/** The contract's analysis header: what the AI Analysis top row shows for the deal. */
export interface FlowAnalysis {
  /** The latest months first shown as tiles (the strip takes the last three). */
  revenueLines: RevenueLine[];
  avgRevenue: string | null;
  leveragePct: number;
  targetPct: number;
  monthlyPayout: number;
}

/**
 * The analysis as Quick Look reads it (`borrowers/<id>/quick-look/`), or null without one. The
 * slot's own source for now: the post-cascade stats payload can replace it without changing the
 * header's props.
 */
export function useFlowAnalysis(applicationId: number, enabled: boolean): FlowAnalysis | null {
  const { data } = useQuery({ ...quickLookQueryOptions(applicationId), enabled });
  const analysis = data?.analysis;
  if (!analysis?.leverage || analysis.revenue_lines.length === 0) return null;
  return {
    revenueLines: analysis.revenue_lines.map(({ month, amount }) => ({
      month_abbrev: month,
      amount,
      // As the AI Analysis top row shows them ("$84.2k"): whole cents do not fit a card's tile.
      amount_display: fmtMoneyShort(amount),
    })),
    avgRevenue: analysis.average_revenue == null ? null : fmtMoneyShort(analysis.average_revenue),
    leveragePct: analysis.leverage.percent,
    targetPct: analysis.leverage.target_percent,
    monthlyPayout: analysis.leverage.monthly_payout,
  };
}

/**
 * The contract body's opening row, as the AI Analysis top row lays it out: Recent revenue (three
 * month tiles and Avg revenue) and Leverage | MCA payout, through the shared `DecisionStrip`. A
 * send has no before → after, so nothing is struck. Nothing without an analysis.
 */
export function FlowAnalysisHeader({ analysis }: { analysis: FlowAnalysis | null }) {
  if (!analysis) return null;
  return (
    <div data-testid="flow-analysis">
      <DecisionStrip
        placement="standalone"
        revenueLines={analysis.revenueLines}
        avgRevenue={analysis.avgRevenue}
        leveragePct={analysis.leveragePct}
        targetPct={analysis.targetPct}
        monthlyPayout={analysis.monthlyPayout}
        payoutByFunder={[]}
      />
    </div>
  );
}
